/**
 * Shared utilities for displaying quiz results (Mixed slots, overlays).
 * Used by CoreSkillsHomework, Homework, ProgressReports, QuestionPage.
 *
 * Linked blank boxes: slots/boxes that share a `linkGroupId` are judged
 * together by multiset equality — any permutation of the authored correct
 * values is accepted. See `docs/linked-blank-boxes-plan.md`.
 */

export function normalizeSlotsToArray(rawSlots) {
  if (Array.isArray(rawSlots)) {
    return [...rawSlots].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  if (rawSlots != null && typeof rawSlots === 'object') {
    const values = Object.values(rawSlots);
    const flat = values.length && Array.isArray(values[0]) ? values.flat() : values;
    return flat.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  return [];
}

export function getMixedSlotDisplayValue(slot, value) {
  if ((slot.slotType === 'dropdown' || slot.slotType === 'multiple-choice') && slot.options?.length) {
    const opt = slot.options.find((o) => o.id === value);
    return opt ? opt.text : value;
  }
  return value ?? '';
}

// Commas are stripped so "5,500" and "5500" compare equal — students and
// authors both reach for thousands separators inconsistently. Inner whitespace
// is preserved so multi-word answers ("ice cream") aren't mangled into "icecream".
function normalizeBlankBoxAnswer(value) {
  return String(value ?? '').toLowerCase().replace(/,/g, '').trim();
}

export function getMixedSlotIsCorrect(slot, value) {
  // Coerce to string before any string ops — protects against non-string
  // values occasionally appearing in state (e.g. numeric echoes from a
  // parent handler), matching the defensive path used for grouped slots.
  const v = String(value ?? '');
  if (slot.slotType === 'blank-box') {
    return normalizeBlankBoxAnswer(v) === normalizeBlankBoxAnswer(slot.answer);
  }
  if (slot.slotType === 'drag-and-drop') {
    return v === String(slot.label ?? '');
  }
  if (slot.slotType === 'dropdown' || slot.slotType === 'multiple-choice') {
    return v === String(slot.correctAnswerId ?? '');
  }
  return false;
}

// --- Link-group helpers -----------------------------------------------------

function isEmptyAnswer(value) {
  return value === undefined || value === null || String(value).trim() === '';
}

function canonicalCorrectValue(slot) {
  if (slot.slotType === 'blank-box') return normalizeBlankBoxAnswer(slot.answer);
  if (slot.slotType === 'drag-and-drop') return String(slot.label ?? '');
  if (slot.slotType === 'dropdown' || slot.slotType === 'multiple-choice') return String(slot.correctAnswerId ?? '');
  return '';
}

function canonicalStudentValue(slot, value) {
  if (slot.slotType === 'blank-box') return normalizeBlankBoxAnswer(value);
  return String(value ?? '');
}

/**
 * Return the list of slots sharing `slot.linkGroupId`, or null if the slot
 * is unlinked or the only member of its group (treat singletons as unlinked).
 */
export function getLinkedGroupMembers(slot, allSlots) {
  const gid = slot?.linkGroupId;
  if (!gid) return null;
  const members = (allSlots ?? []).filter((s) => s?.linkGroupId === gid);
  if (members.length < 2) return null;
  return members;
}

/**
 * Greedy multiset match. Returns a Map keyed by member id with
 * 'matched' | 'unmatched' for each member that has a non-empty answer.
 * Empty answers are omitted from the map.
 */
export function computeGroupMatchStatuses(members, answers) {
  const sorted = [...members].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const remaining = sorted.map(canonicalCorrectValue);
  const result = new Map();
  for (const m of sorted) {
    const raw = answers?.[m.id];
    if (isEmptyAnswer(raw)) continue;
    const student = canonicalStudentValue(m, raw);
    const idx = remaining.indexOf(student);
    if (idx >= 0) {
      remaining[idx] = Symbol('consumed');
      result.set(m.id, 'matched');
    } else {
      result.set(m.id, 'unmatched');
    }
  }
  return result;
}

/**
 * Live border status for a slot as the student types.
 * 'neutral' while empty, 'green' if entry is (provisionally) valid,
 * 'red' if entry can't be reconciled with the authored answer(s).
 */
export function getSlotLiveStatus(slot, allSlots, answers) {
  const raw = answers?.[slot.id];
  if (isEmptyAnswer(raw)) return 'neutral';
  const group = getLinkedGroupMembers(slot, allSlots);
  if (!group) {
    return getMixedSlotIsCorrect(slot, raw) ? 'green' : 'red';
  }
  const statuses = computeGroupMatchStatuses(group, answers);
  return statuses.get(slot.id) === 'matched' ? 'green' : 'red';
}

/**
 * Submit-time correctness for a slot in the context of its whole question.
 * A linked group is correct only when every member has a non-empty answer
 * AND the multisets match.
 */
export function isSlotCorrectInContext(slot, allSlots, answers) {
  const group = getLinkedGroupMembers(slot, allSlots);
  if (!group) {
    return getMixedSlotIsCorrect(slot, answers?.[slot.id]);
  }
  for (const m of group) {
    if (isEmptyAnswer(answers?.[m.id])) return false;
  }
  const statuses = computeGroupMatchStatuses(group, answers);
  for (const m of group) {
    if (statuses.get(m.id) !== 'matched') return false;
  }
  return true;
}

// --- Legacy Blank-Box-Question helpers --------------------------------------
// BlankBox shape is effectively a slot with implicit slotType 'blank-box'.

function boxToSlot(box) {
  return { ...box, slotType: 'blank-box' };
}

export function getBlankBoxLiveStatus(box, allBoxes, answers) {
  return getSlotLiveStatus(boxToSlot(box), (allBoxes ?? []).map(boxToSlot), answers);
}

export function isBlankBoxCorrectInContext(box, allBoxes, answers) {
  return isSlotCorrectInContext(boxToSlot(box), (allBoxes ?? []).map(boxToSlot), answers);
}
